import { InputTypes } from "atoms/constants/input-types";
import { DataTestAttributes } from "interfaces/data-test-attributes";
import { InputProperties } from "interfaces/forms/input-properties";
import React, { forwardRef, Ref, RefObject } from "react";
import StringUtils from "utilities/string-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface TextInputProps
    extends InputProperties,
        Pick<DataTestAttributes, "dataTestId"> {
    hintText?: string;
    id: string;

    /**
     * Max number of characters the input will accept. If not set, input has no character limit.
     */
    maxLength?: number;
    name?: string;
    onMouseUp?: (e: React.MouseEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onSelect?: (e: React.MouseEvent<HTMLInputElement>) => void;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    ref?: RefObject<HTMLInputElement>;
}

// #endregion Interfaces

const BASE_CSS_CLASS = "c-text-input";

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const TextInput = forwardRef(
    (props: TextInputProps, ref: Ref<HTMLInputElement>) => {
        const {
            ariaLabel,
            ariaLabelledBy,
            dataTestId,
            disabled,
            hintText,
            id,
            maxLength,
            name,
            onChange,
            onMouseUp,
            onKeyDown,
            onFocus,
            placeholder,
            value,
            type,
            onSelect,
            onClick,
            onBlur,
            cssClassName = "",
        } = props;

        return (
            <React.Fragment>
                <input
                    onBlur={onBlur}
                    className={cssClassName}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    data-test-id={dataTestId}
                    disabled={disabled}
                    id={id}
                    maxLength={maxLength}
                    name={name}
                    onChange={onChange}
                    onSelect={onSelect}
                    onMouseUp={onMouseUp}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onClick={onClick}
                    placeholder={placeholder}
                    ref={ref}
                    type={type || InputTypes.Text}
                    value={value}
                />
                {StringUtils.hasValue(hintText) && (
                    <div className={`${BASE_CSS_CLASS}__hint-text`}>
                        {hintText}
                    </div>
                )}
            </React.Fragment>
        );
    }
);

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Export
// -----------------------------------------------------------------------------------------

export default TextInput;

// #endregion Export
